import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';
import api from '../services/api';
import AddCoachingCenterModal from '../components/coachingCenters/AddCoachingCenterModal';

function StatusBadge({ isActive }) {
  return (
    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
      isActive ? 'bg-green-100 text-green-700' : 'bg-teacher-bg-secondary text-teacher-text-primary'
    }`}>
      {isActive ? 'ACTIVE' : 'INACTIVE'}
    </span>
  );
}

export default function CoachingCenters() {
  const [centers, setCenters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('active');
  const [search, setSearch] = useState('');
  const [total, setTotal] = useState(0);
  const [showAddModal, setShowAddModal] = useState(false);

  async function load() {
    try {
      setLoading(true);
      const result = await api.getCoachingCenters({ filter, search, limit: 50 });
      setCenters(result.data.centers);
      setTotal(result.data.total);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, [filter, search]);

  function formatDate(d) {
    if (!d) return 'Never';
    try { return formatDistanceToNow(new Date(d), { addSuffix: true }); }
    catch { return 'Unknown'; }
  }

  return (
    <div className="p-6">
      <div className="mb-6 flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-teacher-text-primary">Coaching Centers</h1>
          <p className="text-teacher-text-secondary">Top-level entities owning teachers and batches</p>
        </div>
        <button
          onClick={() => setShowAddModal(true)}
          className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
        >
          + Add Center
        </button>
      </div>

      <AddCoachingCenterModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        onSuccess={load}
      />

      <div className="flex flex-col sm:flex-row gap-4 mb-6">
        <input
          type="text"
          placeholder="Search by name, location, contact email..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="flex-1 px-4 py-2 border border-teacher-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
        />
        <select
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className="px-4 py-2 border border-teacher-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white"
        >
          <option value="all">All Centers</option>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
        </select>
      </div>

      <div className="mb-4 text-sm text-teacher-text-secondary">
        Showing {centers.length} of {total} centers
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700 mb-4">{error}</div>
      )}

      <div className="bg-white rounded-xl shadow-sm border border-teacher-border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-teacher-bg border-b border-teacher-border">
                <th className="px-6 py-3 text-left text-xs font-medium text-teacher-text-secondary uppercase tracking-wider">Name</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-teacher-text-secondary uppercase tracking-wider">Location</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-teacher-text-secondary uppercase tracking-wider">Teachers</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-teacher-text-secondary uppercase tracking-wider">Batches</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-teacher-text-secondary uppercase tracking-wider">Students</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-teacher-text-secondary uppercase tracking-wider">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-teacher-text-secondary uppercase tracking-wider">Created</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-teacher-text-secondary uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {loading ? (
                <tr><td colSpan="8" className="px-6 py-12 text-center text-teacher-text-secondary">Loading centers...</td></tr>
              ) : centers.length === 0 ? (
                <tr><td colSpan="8" className="px-6 py-12 text-center text-teacher-text-secondary">No centers found</td></tr>
              ) : (
                centers.map((c) => (
                  <tr key={c.id} className="hover:bg-teacher-bg">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-teacher-text-primary">{c.name}</div>
                      {c.contact_email && <div className="text-xs text-teacher-text-secondary">{c.contact_email}</div>}
                    </td>
                    <td className="px-6 py-4 text-sm text-teacher-text-secondary">{c.location || '-'}</td>
                    <td className="px-6 py-4 text-sm font-medium text-primary-600">{c.total_teachers || 0}</td>
                    <td className="px-6 py-4 text-sm font-medium text-primary-600">{c.total_batches || 0}</td>
                    <td className="px-6 py-4 text-sm font-medium text-primary-600">{c.total_students || 0}</td>
                    <td className="px-6 py-4 whitespace-nowrap"><StatusBadge isActive={c.is_active} /></td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-teacher-text-secondary">{formatDate(c.created_at)}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <Link to={`/coaching-centers/${c.id}`} className="text-primary-600 hover:text-primary-900">View Details</Link>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
